"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { BATCH_START_FOR_PART2_ROWS_EA } from "@/lib/utils";

const DESKTOP_IMG =
  "https://ik.imagekit.io/mileseducation/miles_website/ea/new/ea_gameplan_desktop.webp";
const MOBILE_IMG =
  "https://ik.imagekit.io/mileseducation/miles_website/ea/new/ea_gameplan_mobile.webp";

/* One live schedule, so no tab strip: this is the batch whose start date the
   section advertises above the button (24 Oct 2026). The part 1 and part 3
   rows still live in lib/utils — bring the tabs back from git history if a
   second batch is ever open at the same time. */
const scheduleData = BATCH_START_FOR_PART2_ROWS_EA;
const scheduleHeading = "EA Schedules";

const EA_gameplan = () => {
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);

  const openSchedule = () => setIsScheduleOpen(true);
  const closeSchedule = () => setIsScheduleOpen(false);

  useEffect(() => {
    if (isScheduleOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isScheduleOpen]);

  return (
    <section className="bg-white py-8 px-4 max-md:bg-[#f3f3f3] max-md:p-4">
      <div className="main_container py-5 bg-white rounded-2xl max-md:px-4 max-md:py-6">
        <h2 className="text-center text-[32px] font-bold text-[#1d1d1d] m-0 mb-[30px] leading-[1.3] max-md:text-2xl max-md:mb-6 md:block hidden">
          AI-Integrated U.S. <span className="text-[#39f]">EA Roadmap</span>
        </h2>
        <h2 className="text-center text-[32px] font-bold text-[#1d1d1d] m-0 mb-[30px] leading-[1.3] max-md:text-2xl max-md:mb-6 md:hidden block">
          <span className="text-[#39f]">AI-Integrated U.S. EA </span> Designed
          for Working
          <br /> Professionals
        </h2>
        <p className="text-center text-[15px] text-[#888] m-0 mb-10 font-semibold leading-[1.5] max-md:text-[13px] max-md:mb-6">
          Stand out to Big 4 & MNCs within 4 months with CAIRA Level 1 badge on
          LinkedIn
        </p>
        <div className="flex justify-center">
          <Image
            src={DESKTOP_IMG}
            alt="AI-Integrated U.S. CPA Roadmap"
            width={1300}
            height={520}
            className="w-full max-w-[1300px] h-auto object-contain max-md:hidden"
            loading="lazy"
          />
          <Image
            src={MOBILE_IMG}
            alt="AI-Integrated U.S. CPA Roadmap"
            width={600}
            height={900}
            className="hidden w-full h-auto object-contain max-md:block"
            loading="lazy"
          />
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 max-md:mt-6 max-md:gap-2.5">
          <h3 className="text-[24px] font-bold text-[#1d1d1d] text-center leading-[1.3] max-md:text-[18px]">
            Next Batch Starts on{" "}
            <span className="text-[#39f]">08 Aug 2026</span>
          </h3>
          <button
            type="button"
            onClick={openSchedule}
            className="md:mt-4 mt-2 !min-w-[306px] px-[28px] py-[12px] border-0 rounded-tl-[10.85px] rounded-tr-[10.85px] rounded-br-[10.85px] rounded-bl-none bg-[#2a85ff] text-white text-[16px] font-bold leading-none cursor-pointer max-md:w-[calc(100%-32px)] max-md:min-w-0 max-md:px-4"
          >
            View Schedule
          </button>
        </div>
      </div>

      {isScheduleOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999] p-4 lg:p-10">
          <div className="rounded-[14px] shadow-lg relative w-full max-w-[850px] h-auto mx-auto bg-white">
            <div className="bg-[#093E7B] px-[15px] py-[8px] relative rounded-t-[12px]">
              <h3 className="text-white text-center text-[18px] lg:text-[24px] leading-[28px] font-semibold pt-2 pb-[8px]">
                {scheduleHeading}
              </h3>
              <button
                type="button"
                onClick={closeSchedule}
                aria-label="Close EA schedule modal"
                className="mt-4 text-[#878787] rounded-full absolute right-[15px] top-[0px] z-[9999]"
              >
                <Image
                  quality={50}
                  src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/close.svg"
                  alt="close"
                  width={24}
                  height={24}
                  loading="lazy"
                />
              </button>
            </div>

            <div className="max-h-[70vh] lg:h-[550px] scrollbar-hide overflow-y-auto rounded-b-[12px] border-t-[0.5px] border-[#185195]">
              <div className="rounded-xl bg-white">
                <table className="border-collapse table-auto w-full">
                  <thead className="sticky top-0 px-[15px] pb-[15px]">
                    <tr className="text-center bg-[#093E7B]">
                      <th className="pl-2 lg:pl-4 py-2 text-[14px] lg:text-[18px] font-semibold text-white w-[10%]">
                        Class
                      </th>
                      <th className="text-[13px] lg:text-[16px] font-semibold text-white">
                        Date
                      </th>
                      <th className="text-[13px] lg:text-[16px] font-semibold text-white">
                        Day
                      </th>
                      <th className="text-[13px] lg:text-[16px] font-semibold text-white leading-[1.2]">
                        Time IST <br />
                        <span className="text-[11px] lg:text-[14px] font-normal">
                          (GMT +5:30 HRS)
                        </span>
                      </th>
                      <th className="py-2 pr-2 lg:pr-4 text-[14px] lg:text-[18px] font-semibold text-white w-[32%]">
                        Class Schedule
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {scheduleData?.map((item, index) => (
                      <ScheduleRow key={index} {...item} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const ScheduleRow = ({
  number,
  date,
  day,
  time,
  description,
}: {
  number?: string;
  date: string;
  day: string;
  time: string;
  description: string | string[];
}) => {
  return (
    <tr className="text-center text-[13px] lg:text-[16px] text-[#061A2F] font-normal border-b border-[#0B346214]/8">
      {number ? <td className="pl-2 lg:pl-4 py-3 lg:py-4">{number}</td> : null}
      <td className="px-2 lg:px-4">{date}</td>
      <td className="px-2 lg:px-4">{day}</td>
      <td className="px-2 lg:px-4">{time}</td>
      <td className="pr-2 lg:pr-4 py-3 lg:py-4">
        {Array.isArray(description)
          ? description.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < description.length - 1 && <br />}
              </React.Fragment>
            ))
          : description}
      </td>
    </tr>
  );
};

export default EA_gameplan;
